import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  PaymentType,
  PaymentsRoutesResponse,
  ToggleSwitch,
  TrustPaymentsCreateSessionSuccess,
  TrustPaymentsFxQuoteFx,
  VerificationType,
  trustPaymentsService,
  v4Service,
} from "src/api";
import { useDepositWithdrawContext, useUrlParamsContext } from "src/contexts";
import { useCalculatedLimits } from "./useCalculatedLimits";

interface RouteMeta {
  source_currency_id?: string;
  target_currency_id?: string;
  fx_enabled?: boolean;
}

// Conversion quotes are TTL-bound on the BE (`ttl: 30`); refresh just
// before they expire so the displayed price never goes stale.
const QUOTE_REFRESH_INTERVAL_SEC = 30;

// State + actions for the Trust Payments withdrawal form. Two-step:
//   1. User types amount → we create a session with a JWT signed for
//      `ACCOUNTCHECK` (zero-value card validation, no money moves).
//   2. SecureTrading SDK collects PAN in its iframe, runs ACCOUNTCHECK,
//      returns a `transactionreference`. We pass that ref to BE
//      `/withdrawal` which issues the real CFT REFUND server-side.
// No PAN ever crosses our boundary — PCI scope stays SAQ-A-EP.
export const useTrustPaymentsWithdrawal = (route: PaymentsRoutesResponse) => {
  const { paymentRouteId, walletId } = useUrlParamsContext();
  const { balance } = useDepositWithdrawContext();
  const availableBalance = balance?.free_balance ?? 0;
  // Live per-period limits (used/limit/remaining) for the wallet currency.
  // `availableLimit` is Infinity when no cap is set — never blocks then.
  const {
    limitsStatus,
    availableLimit,
    loading: limitsLoading,
    currencyId: limitsCurrency,
    refetch: refetchLimits,
  } = useCalculatedLimits(PaymentType.Withdrawal);

  const routeMeta = useMemo<RouteMeta>(() => {
    try {
      return route.meta ? (JSON.parse(route.meta) as RouteMeta) : {};
    } catch {
      return {};
    }
  }, [route.meta]);

  const sourceCurrency = routeMeta.source_currency_id ?? route.currency_id ?? "";
  const targetCurrency = routeMeta.target_currency_id ?? route.currency_id ?? "";
  const targetCurrencyPrecision = route.currency?.precision ?? balance?.currency?.precision ?? 2;
  const sourceCurrencyPrecision = sourceCurrency === targetCurrency ? targetCurrencyPrecision : 2;
  const fxEnabled = routeMeta.fx_enabled === true;

  const [amount, setAmount] = useState<string>("");
  const [amountTouched, setAmountTouched] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [mfaToken, setMfaToken] = useState("");
  const [mfaTouched, setMfaTouched] = useState(false);
  // Quote-driven state. Same `create_conversion_quote` (dry_run +
  // fees_in_price) the BE will execute on the Trust REFUND — so the
  // numbers in the form are the exact numbers that hit the wallet,
  // not an approximation.
  const [platformFee, setPlatformFee] = useState<number>(0);
  const [feeCurrency, setFeeCurrency] = useState<string>("");
  const [estimatedReceive, setEstimatedReceive] = useState<number>(0);
  const [price, setPrice] = useState<number>(0);
  const [quoteLoading, setQuoteLoading] = useState(false);
  const [secondsUntilRefresh, setSecondsUntilRefresh] = useState<number>(QUOTE_REFRESH_INTERVAL_SEC);
  // FX-only state. Non-FX routes leave both null and behave exactly
  // as the legacy flow.
  const [fxQuote, setFxQuote] = useState<TrustPaymentsFxQuoteFx | null>(null);
  const [fxError, setFxError] = useState<string | null>(null);
  // Terminal state set once Trust returns the REFUND response from BE.
  const [completed, setCompleted] = useState<{
    status: "success" | "error";
    message?: string;
  } | null>(null);

  // `amount` is the TARGET crypto (USDC/EURC) — "Sell Crypto" flow.
  const targetAmount = Number(amount);
  const amountPositive = Number.isFinite(targetAmount) && targetAmount > 0;
  const insufficientBalance = amountPositive && targetAmount > availableBalance;
  const amountValid = amountPositive && !insufficientBalance;
  const totpRequired = route.verification_type === VerificationType.Totp;
  const mfaValid = !totpRequired || /^\d{6}$/.test(mfaToken);

  const fetchQuote = useCallback(async () => {
    // Withdrawal is the reverse leg of the route: user sells the
    // TARGET stablecoin (USDC/EURC) and the BE refunds the SOURCE
    // fiat (USD/EUR) onto their card.
    //
    // Conversion quote gives the FX price; the platform fee comes
    // from `estimate_payment_fee` (PaymentFee.withdrawal_progressive_fee)
    // because that's where the route's progressive % is configured in
    // back-office — `quote.fee_currency_amount` only carries the
    // trading-fee leg, which is 0 with `fees_in_price: true`.
    setQuoteLoading(true);
    setFxError(null);
    try {
      if (fxEnabled) {
        const response = await trustPaymentsService.quoteWithdrawal({
          payment_route_id: paymentRouteId,
          amount: targetAmount,
        });
        if (response.fx) {
          setFxQuote(response);
          setPlatformFee(response.fees.platform_fee);
          setFeeCurrency(targetCurrency);
          setEstimatedReceive(response.local_amount);
          setPrice(response.fx_rate_effective);
        } else {
          setFxQuote(null);
          setPlatformFee(response.fees.platform_fee);
          setFeeCurrency(targetCurrency);
          setEstimatedReceive(response.total_base_amount);
          setPrice(0);
          if (response.reason === "fx_provider_unavailable") {
            setFxError("FX_PROVIDER_UNAVAILABLE");
          }
        }
        setSecondsUntilRefresh(QUOTE_REFRESH_INTERVAL_SEC);
      } else {
        const [quote, paymentFee] = await Promise.all([
          v4Service.createConversionQuote({
            source_currency_id: targetCurrency,
            target_currency_id: sourceCurrency,
            source_currency_amount: targetAmount,
            fees_in_price: true,
            dry_run: ToggleSwitch.On,
            ttl: 30,
          }),
          v4Service.estimatePaymentFee({
            payment_route_id: paymentRouteId,
            type: PaymentType.Withdrawal,
            amount: targetAmount,
          }),
        ]);
        const fee = Number(paymentFee) || 0;
        const grossReceive = Number(quote.target_currency_amount) || 0;
        // Fee is in target stablecoin (USDC), receive is in source fiat
        // (USD). Keep the FX ratio from the quote and apply it to the
        // net stablecoin amount so "You will receive" matches what BE
        // will actually refund after the withdrawal fee is deducted.
        const netReceive =
          targetAmount > 0 && grossReceive > 0
            ? (Math.max(targetAmount - fee, 0) / targetAmount) * grossReceive
            : grossReceive;
        setPlatformFee(fee);
        setFeeCurrency(targetCurrency);
        setEstimatedReceive(netReceive);
        setPrice(Number(quote.price) || 0);
        setSecondsUntilRefresh(QUOTE_REFRESH_INTERVAL_SEC);
      }
    } catch {
      setPlatformFee(0);
      setFeeCurrency("");
      setEstimatedReceive(0);
      setPrice(0);
      setFxQuote(null);
      if (fxEnabled) setFxError("FX_QUOTE_FAILED");
    } finally {
      setQuoteLoading(false);
    }
  }, [paymentRouteId, sourceCurrency, targetCurrency, targetAmount, fxEnabled]);

  const quoteDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => {
    if (!paymentRouteId || !sourceCurrency || !targetCurrency || !amountValid) {
      setPlatformFee(0);
      setFeeCurrency("");
      setEstimatedReceive(0);
      setPrice(0);
      setQuoteLoading(false);
      setSecondsUntilRefresh(QUOTE_REFRESH_INTERVAL_SEC);
      setFxQuote(null);
      setFxError(null);
      return;
    }
    setQuoteLoading(true);
    quoteDebounceRef.current = setTimeout(fetchQuote, 300);
    return () => {
      if (quoteDebounceRef.current) clearTimeout(quoteDebounceRef.current);
    };
  }, [paymentRouteId, sourceCurrency, targetCurrency, amountValid, fetchQuote]);

  // Auto-refresh: the conversion quote BE-side TTL is 30s — re-fetch
  // just before it expires so the price the user sees stays the price
  // we'll actually settle on. Pauses once the card step takes over
  // (`sessionId !== null`) since the amount is locked in by then.
  useEffect(() => {
    if (!paymentRouteId || !amountValid || sessionId !== null) return;
    const interval = setInterval(() => {
      setSecondsUntilRefresh((prev) => {
        if (prev <= 1) {
          fetchQuote();
          return QUOTE_REFRESH_INTERVAL_SEC;
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(interval);
  }, [paymentRouteId, amountValid, sessionId, fetchQuote]);

  // Block submission when the platform fee swallows the entire input
  // (e.g. flat fee 20 USDC, user typed 1 USDC). Wait on the quote to
  // land first — `quoteLoading` true means we have no reliable fee
  // to compare against yet.
  const belowMinimum = amountValid && !quoteLoading && platformFee > 0 && estimatedReceive <= 0;

  // The amount sold is the wallet/notion currency, directly comparable to
  // availableLimit. Infinity (no cap) keeps this false.
  const limitExceeded = Number.isFinite(availableLimit) && targetAmount > 0 && targetAmount > availableLimit;

  // Single source of truth for "can't submit" — drives both the button's
  // disabled state and the Enter-key submit() guard, so neither path can
  // create a session while the form is in an invalid/pending state.
  const cannotSubmit =
    !paymentRouteId ||
    !amountValid ||
    belowMinimum ||
    limitExceeded ||
    !mfaValid ||
    quoteLoading ||
    loading ||
    sessionId !== null ||
    (fxEnabled && !!fxError);

  const submitDisabled = cannotSubmit;

  const handleAmountChange = useCallback(
    (value: string) => {
      const [integerPart = "", ...decimalParts] = value.replace(/[^0-9.]/g, "").split(".");
      const decimalPart = decimalParts.join("").slice(0, targetCurrencyPrecision);
      const sanitized = decimalParts.length ? `${integerPart}.${decimalPart}` : integerPart;
      setAmount(sanitized);
      setError(null);
    },
    [targetCurrencyPrecision]
  );

  const handleMfaTokenChange = useCallback((value: string) => {
    setMfaToken(value.replace(/\D/g, "").slice(0, 6));
    setError(null);
  }, []);

  const submit = useCallback(async () => {
    if (cannotSubmit) return;
    setLoading(true);
    setError(null);
    try {
      const response = await trustPaymentsService.createSession({
        data: {
          payment_route_id: paymentRouteId,
          amount: targetAmount,
          type: "withdrawal",
          ...(totpRequired ? { mfa_token: mfaToken } : {}),
          ...(walletId ? { wallet_id: walletId } : {}),
          ...(fxQuote ? { quote_id: fxQuote.quote_id } : {}),
        },
      });

      if (!response.success) {
        setError(response.error || "TRUSTPAYMENTS_SESSION_FAILED");
        return;
      }

      const ok = response as TrustPaymentsCreateSessionSuccess;
      setSessionId(ok.session_id);
    } catch (e) {
      const msg = e instanceof Error ? e.message : String(e);
      setError(msg);
    } finally {
      setLoading(false);
    }
  }, [cannotSubmit, paymentRouteId, targetAmount, totpRequired, mfaToken, walletId, fxQuote]);

  // Called by the SDK card form once ACCOUNTCHECK returns a
  // `transactionreference` we can use as `parenttransactionreference`
  // on the BE-side REFUND.
  const submitWithdrawalWithRef = useCallback(
    async (parentTransactionReference: string) => {
      if (!sessionId) return;
      try {
        const result = await trustPaymentsService.createWithdrawal({
          session_id: sessionId,
          parent_transaction_reference: parentTransactionReference,
        });
        if (result.success) {
          setCompleted({ status: "success" });
        } else {
          setCompleted({
            status: "error",
            message: result.error || "TRUSTPAYMENTS_WITHDRAWAL_FAILED",
          });
        }
      } catch (e) {
        setCompleted({
          status: "error",
          message: e instanceof Error ? e.message : String(e),
        });
      }
    },
    [sessionId]
  );

  const reset = useCallback(() => {
    setSessionId(null);
    setCompleted(null);
    setAmount("");
    setAmountTouched(false);
    setMfaToken("");
    setMfaTouched(false);
    setError(null);
    setFxQuote(null);
    setFxError(null);
    // The withdrawal just consumed limit headroom — pull fresh used totals
    // for the form we're returning to.
    refetchLimits();
  }, [refetchLimits]);

  return {
    sourceCurrency,
    targetCurrency,
    sourceCurrencyPrecision,
    targetCurrencyPrecision,
    amount,
    handleAmountChange,
    amountTouched,
    setAmountTouched,
    amountValid,
    amountPositive,
    insufficientBalance,
    belowMinimum,
    availableBalance,
    targetAmount,
    totpRequired,
    mfaToken,
    handleMfaTokenChange,
    mfaTouched,
    setMfaTouched,
    mfaValid,
    loading,
    error,
    sessionId,
    completed,
    submit,
    submitWithdrawalWithRef,
    submitDisabled,
    reset,
    platformFee,
    feeCurrency,
    quoteLoading,
    estimatedReceive,
    price,
    secondsUntilRefresh,
    fxEnabled,
    fxQuote,
    fxError,
    limitsStatus,
    availableLimit,
    limitsLoading,
    limitsCurrency,
    limitExceeded,
  };
};
